import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { useDateien, useUploadDatei } from '../hooks/data.js';
import { Stand } from '../components/Stand.jsx';
import { useToast, withHinweis } from '../state/toast.jsx';
import { fileUrl } from '../lib/api.js';
import { dayLabel } from '../lib/format.js';
import { extClass, extOf, isImageName, photoName, prepareUpload } from '../lib/files.js';
import { Icon } from '../components/Icon.jsx';
import { FileViewer } from '../sheets/FileViewer.jsx';

const FOTO_ORDNER = 'Fotos';

/** Ordnerstruktur aus der flachen Exxas-Liste (ordnerId = Elternordner). Ordner-ID steht in der URL. */
export function Files() {
  const { nr, base } = useAuftragCtx();
  const params = useParams();
  const folderId = params['*'] || null;
  const navigate = useNavigate();
  const dateien = useDateien(nr);
  const upload = useUploadDatei(nr);
  const toast = useToast();
  const photoInput = useRef(null);
  const fileInput = useRef(null);
  const [q, setQ] = useState('');
  const [viewer, setViewer] = useState(null);

  const all = useMemo(() => dateien.data ?? [], [dateien.data]);
  const byId = useMemo(() => new Map(all.map((f) => [f.id, f])), [all]);
  const pathOf = (id) => {
    const trail = [];
    let cur = id ? byId.get(id) : null;
    while (cur) { trail.unshift(cur); cur = cur.ordnerId ? byId.get(cur.ordnerId) : null; }
    return trail;
  };
  const trail = pathOf(folderId);
  const where = (f) => [nr, ...pathOf(f.ordnerId).map((x) => x.name)].join(' / ');
  const open = (id) => navigate(id ? `${base}/dateien/${id}` : `${base}/dateien`);

  const children = all.filter((f) => (f.ordnerId ?? null) === folderId);
  const folders = children.filter((f) => f.istOrdner).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const files = children.filter((f) => !f.istOrdner).sort((a, b) => String(b.datum).localeCompare(String(a.datum)));
  const photos = folderId ? files.filter((f) => isImageName(f.name)) : all.filter((f) => !f.istOrdner && isImageName(f.name)).sort((a, b) => String(b.datum).localeCompare(String(a.datum))).slice(0, 12);
  const docs = folderId ? files.filter((f) => !isImageName(f.name)) : files.filter((f) => !isImageName(f.name));
  const hits = q.trim() ? all.filter((f) => !f.istOrdner && f.name.toLowerCase().includes(q.trim().toLowerCase())) : [];
  const countIn = (id) => all.filter((f) => !f.istOrdner && pathOf(f.ordnerId).some((x) => x.id === id)).length;

  const doUpload = async (e, kind) => {
    const list = [...(e.target.files ?? [])];
    e.target.value = '';
    for (const file of list) {
      try {
        const up = await prepareUpload(file);
        const fileName = kind === 'photo' ? photoName() : up.fileName;
        const target = folderId ? { ordnerId: folderId } : kind === 'photo' ? { ordnerName: FOTO_ORDNER } : {};
        const res = await upload.mutateAsync({ fileName, base64: up.base64, ...target });
        toast(withHinweis(`${fileName} hochgeladen`, res?.hinweis));
      } catch (err) {
        toast({ text: `Upload fehlgeschlagen: ${err.message}`, error: true });
      }
    }
  };

  return (
    <div className="stack" style={{ gap: 14 }}>
      <Stand abfragen={[dateien]} />
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <label className="search" style={{ flex: '1 1 260px' }}>
          <Icon name="search" stroke={2.2} />
          <span className="vh">Dateien suchen</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pläne, Fotos, Dokumente suchen" />
        </label>
        <button className="btn" onClick={() => photoInput.current?.click()} disabled={upload.isPending}><Icon name="camera" />Foto</button>
        <button className="btn secondary" onClick={() => fileInput.current?.click()} disabled={upload.isPending} aria-label="Datei hochladen"><Icon name="upload" /></button>
        <input ref={photoInput} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => doUpload(e, 'photo')} />
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => doUpload(e, 'file')} />
      </div>

      {dateien.isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {dateien.isError && <div className="empty" role="alert">{dateien.error.message}</div>}

      {q.trim() ? (
        <FileList title={`${hits.length} Treffer in ${nr}`} files={hits} meta={(f) => `${where(f)} · ${dayLabel(f.datum?.slice(0, 10))}`} onOpen={setViewer} />
      ) : !dateien.isLoading && (
        <>
          <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
            {folderId && <button className="iconbtn white" aria-label="Ordner zurück" onClick={() => open(trail.length > 1 ? trail[trail.length - 2].id : null)}><Icon name="back" stroke={2.4} /></button>}
            <nav aria-label="Ordnerpfad" className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              <button className="linkbtn" style={{ color: trail.length ? 'var(--muted)' : 'var(--ink)', padding: '8px 4px', fontSize: 14 }} onClick={() => open(null)}>{nr}</button>
              {trail.map((f, i) => (
                <span key={f.id} className="row" style={{ gap: 4 }}>
                  <span aria-hidden="true" style={{ color: 'var(--faint)' }}>/</span>
                  <button className="linkbtn" aria-current={i === trail.length - 1 ? 'page' : undefined} style={{ color: i === trail.length - 1 ? 'var(--ink)' : 'var(--muted)', padding: '8px 4px', fontSize: 14 }} onClick={() => open(f.id)}>{f.name}</button>
                </span>
              ))}
            </nav>
          </div>

          {folders.length > 0 && (
            <div className="fgrid">
              {folders.map((f) => (
                <button key={f.id} className="card" style={{ border: 'none', textAlign: 'left', padding: 14 }} onClick={() => open(f.id)}>
                  <span className="ext folder" style={{ width: 44, height: 44 }}><Icon name="folder" size={22} /></span>
                  <span className="stack" style={{ gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{f.name}</span>
                    <span className="sub" style={{ fontSize: 12 }}>{countIn(f.id)} Dateien</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {photos.length > 0 && (
            <section className="stack" style={{ gap: 8 }}>
              <h2 className="h2" style={{ margin: '0 4px' }}>{folderId ? `${photos.length} Fotos` : 'Neueste Bilder'}</h2>
              <div className="pgrid">
                {photos.map((p) => (
                  <button key={p.id} className="photo" onClick={() => setViewer(p)} aria-label={`Foto ${p.name} öffnen`}>
                    <img src={fileUrl(p.id)} alt="" loading="lazy" />
                    <span className="pcap">{p.name}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {docs.length > 0 && <FileList title={folderId ? `${docs.length} Dateien` : 'Dateien im Hauptordner'} files={docs} meta={(f) => dayLabel(f.datum?.slice(0, 10))} onOpen={setViewer} />}
          {folderId && !folders.length && !files.length && <div className="empty">Dieser Ordner ist leer.</div>}
          {!folderId && !all.length && <div className="empty">Zu diesem Auftrag gibt es noch keine Dateien.</div>}
        </>
      )}

      {viewer && <FileViewer file={viewer} where={where(viewer)} onClose={() => setViewer(null)} />}
    </div>
  );
}

function FileList({ title, files, meta, onOpen }) {
  return (
    <section className="stack" style={{ gap: 8 }}>
      <h2 className="h2" style={{ margin: '0 4px' }}>{title}</h2>
      <div className="card list" style={{ padding: '2px 12px' }}>
        {files.map((f) => (
          <button key={f.id} className="listrow" style={{ border: 'none', borderTop: '1px solid var(--line-2)', background: 'transparent', textAlign: 'left', width: '100%' }} onClick={() => onOpen(f)}>
            <span className={`ext ${extClass(f.name)}`}>{extOf(f.name)}</span>
            <span className="stack" style={{ flex: 1, gap: 2 }}>
              <span className="ellipsis" style={{ fontSize: 15, fontWeight: 700 }}>{f.name}</span>
              <span className="ellipsis sub" style={{ fontSize: 12 }}>{meta(f)}</span>
            </span>
            <Icon name="chevron" size={18} stroke={2.2} style={{ color: 'var(--faint)' }} />
          </button>
        ))}
      </div>
    </section>
  );
}
